/**
 * Teacher API Service
 * Handles all API calls to the backend /teacher endpoints.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class TeacherAPI {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}/teacher${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      if (response.status === 403) {
        throw new Error('Access denied - Teacher privileges required');
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Request failed');
      }

      return response.json();
    } catch (error) {
      console.error(`[TeacherAPI] ${endpoint} failed:`, error);
      throw error;
    }
  }

  // Teacher profile + school summary
  async getMe() {
    return this.request('/me');
  }

  // Roster of lean student cards
  async getStudents() {
    return this.request('/students');
  }

  // Drill-down for one student (stats + activity + grammarHistory)
  async getStudent(uid) {
    return this.request(`/students/${uid}`);
  }

  // Recent conversation messages (privacy-gated server-side)
  async getStudentConversations(uid, limit = 20) {
    return this.request(`/students/${uid}/conversations?limit=${limit}`);
  }

  // Class-wide weak-point aggregation for lesson planning ("Teach Next")
  async getClassInsights() {
    return this.request('/class-insights');
  }

  // Send a push notification to a specific student in the teacher's school.
  // type: 'educational' (default) | 'personal' | 'achievement' | 'system' | 'promotional'
  async notifyStudent(uid, { title, message, type = 'educational' }) {
    return this.request(`/students/${uid}/notify`, {
      method: 'POST',
      body: JSON.stringify({ title, message, type }),
    });
  }
}

const teacherAPI = new TeacherAPI();
export default teacherAPI;
